import { useState, useEffect } from 'react'
import {
  getSavedTracks,
  getSavedMarkers,
  deleteTrack,
  deleteMarker,
  exportTrackAsGeoJSON,
  exportTrackAsGPX,
  exportMarkersAsGeoJSON,
  SavedTrack,
  SavedMarker,
} from '../utils/storage'
import './TrackManager.css'

interface TrackManagerProps {
  onLoadTrack: (track: SavedTrack) => void
  onLoadMarkers: (markers: SavedMarker[]) => void
  onFlyToMarker: (coordinates: [number, number]) => void
}

function TrackManager({ onLoadTrack, onLoadMarkers, onFlyToMarker }: TrackManagerProps) {
  const [tracks, setTracks] = useState<SavedTrack[]>([])
  const [markers, setMarkers] = useState<SavedMarker[]>([])
  const [activeTab, setActiveTab] = useState<'tracks' | 'markers'>('tracks')

  // Load tracks and markers on mount
  useEffect(() => {
    refreshData()
  }, [])

  const refreshData = () => {
    setTracks(getSavedTracks())
    setMarkers(getSavedMarkers())
  }

  const handleDeleteTrack = (id: string) => {
    if (confirm('Delete this track?')) {
      deleteTrack(id)
      refreshData()
    }
  }

  const handleDeleteMarker = (id: string) => {
    if (confirm('Delete this marker?')) {
      deleteMarker(id)
      refreshData()
      // Refresh markers on map
      onLoadMarkers(getSavedMarkers())
    }
  }

  const handleExportTrack = (track: SavedTrack, format: 'geojson' | 'gpx') => {
    const data = format === 'geojson' ? exportTrackAsGeoJSON(track) : exportTrackAsGPX(track)
    const ext = format === 'geojson' ? 'geojson' : 'gpx'
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${track.name.replace(/\s/g, '_')}.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExportAllMarkers = () => {
    if (markers.length === 0) {
      alert('No markers to export')
      return
    }
    const data = exportMarkersAsGeoJSON(markers)
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'navica_markers.geojson'
    a.click()
    URL.revokeObjectURL(url)
  }

  const formatDistance = (meters: number) => {
    if (meters < 1000) return `${meters.toFixed(0)}m`
    return `${(meters / 1000).toFixed(2)}km`
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  const formatSpeed = (ms: number) => {
    const knots = ms * 1.94384
    return `${knots.toFixed(1)} kts`
  }

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
  }

  return (
    <div className="track-manager">
      <div className="track-manager-header">
        <button
          className={`tab-btn ${activeTab === 'tracks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracks')}
        >
          📍 Tracks ({tracks.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'markers' ? 'active' : ''}`}
          onClick={() => setActiveTab('markers')}
        >
          🎯 Markers ({markers.length})
        </button>
      </div>

      {activeTab === 'tracks' && (
        <div className="tracks-list">
          {tracks.length === 0 ? (
            <div className="empty-state">
              <p>No saved tracks yet</p>
              <p className="hint">Start tracking your GPS to save tracks</p>
            </div>
          ) : (
            tracks
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .map((track) => (
                <div key={track.id} className="track-item">
                  <div className="track-header">
                    <h3>{track.name}</h3>
                    <span className="track-date">{formatDate(track.date)}</span>
                  </div>
                  <div className="track-stats">
                    <div className="stat">
                      <span className="stat-label">Distance:</span>
                      <span className="stat-value">{formatDistance(track.distance)}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Duration:</span>
                      <span className="stat-value">{formatDuration(track.duration)}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Max Speed:</span>
                      <span className="stat-value">{formatSpeed(track.maxSpeed)}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Avg Speed:</span>
                      <span className="stat-value">{formatSpeed(track.avgSpeed)}</span>
                    </div>
                  </div>
                  <div className="track-actions">
                    <button
                      className="action-btn load-btn"
                      onClick={() => onLoadTrack(track)}
                      title="Load track on map"
                    >
                      📍 Load
                    </button>
                    <button
                      className="action-btn export-btn"
                      onClick={() => handleExportTrack(track, 'geojson')}
                      title="Export as GeoJSON"
                    >
                      💾 GeoJSON
                    </button>
                    <button
                      className="action-btn export-btn"
                      onClick={() => handleExportTrack(track, 'gpx')}
                      title="Export as GPX"
                    >
                      💾 GPX
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteTrack(track.id)}
                      title="Delete track"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {activeTab === 'markers' && (
        <div className="markers-list">
          <div className="markers-header">
            {markers.length > 0 && (
              <button className="export-all-btn" onClick={handleExportAllMarkers}>
                💾 Export All Markers
              </button>
            )}
          </div>
          {markers.length === 0 ? (
            <div className="empty-state">
              <p>No saved markers yet</p>
              <p className="hint">Right-click on map to add markers</p>
            </div>
          ) : (
            markers
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .map((marker) => (
                <div key={marker.id} className="marker-item">
                  <div className="marker-header">
                    <h3>{marker.name}</h3>
                    <span className="marker-date">{formatDate(marker.date)}</span>
                  </div>
                  <div className="marker-info">
                    <div className="marker-coords">
                      📍 {marker.coordinates[1].toFixed(6)}, {marker.coordinates[0].toFixed(6)}
                    </div>
                    {marker.depth && (
                      <div className="marker-depth">🌊 Depth: {marker.depth.toFixed(1)}m</div>
                    )}
                    {marker.notes && <div className="marker-notes">📝 {marker.notes}</div>}
                  </div>
                  <div className="marker-actions">
                    <button
                      className="action-btn goto-btn"
                      onClick={() => onFlyToMarker(marker.coordinates)}
                      title="Go to marker"
                    >
                      🎯 Go To
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteMarker(marker.id)}
                      title="Delete marker"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      )}
    </div>
  )
}

export default TrackManager
